import React, { useEffect, useState } from "react";
import {
  MotifButton,
  MotifFileUploader,
  MotifFileUploaderItem,
  MotifIcon,
  MotifModal,
  MotifModalBody,
  MotifModalFooter,
  MotifModalHeader,
} from "@ey-xd/motif-react";
import {
  actionIcDescription24px,
} from "@ey-xd/motif-react/assets/icons";
import { useTranslation } from "react-i18next";
import {
  useLazyGetFileImportProgressStatusQuery,
} from "@/services/manualDataSource/ManualDataSourceReducer";
import { useParams } from "react-router-dom";
import SuccessMessage from "@/pages/CaseDetail/Distributions/ManualDataSource/SuccessMessage/SuccessMessage";
import DataSourceUploadStatus from "@/pages/CaseDetail/Distributions/ManualDataSource/UploadStatus/DataSoureUploadStatus";
import ErrorMessage from "@/pages/CaseDetail/Distributions/ManualDataSource/ErrorMessage/ErrorMessage";
import ConfirmationModal from "@/components/Modal/ConfirmationModal/ConfirmationModal";
import { fileExtensions } from "@/services/documents/documentsReducer";
import { CANCEL_BUTTON_VARIANT, DOWNLOAD_IMG_VARIANT, getCommonDownloadImg, UPLOAD_BUTTON_VARIANT } from "@/components/Images/CommonImages";
import { useBulkImportDocumentMutation } from "@/services/import/bulkImportReducer";
import CommonTooltipWrapper from "@/components/Tooltip/CommonTooltipWrapper";
import NotificationGrid from "@/components/Notification/NotificationGrid";

interface BulkUploadProps {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  title: string;
  description: string;
  uploadLabel: string;
  uploadDescription: string;
  accept: string;
  maxFiles: number;
  onDownload: (entityType?: string) => void;
  onClose: () => void;
  onBulkCreateSuccess?: () => void;
  templateDownloadError?: string;
  templetDowmloadLoading?: boolean;
  setTemplateDownloadError?: (message: string) => void;
}

const ClaimBulkImport = ({
  showModal,
  setShowModal,
  title,
  description,
  uploadLabel,
  uploadDescription,
  accept,
  maxFiles,
  onDownload,
  onClose,
  onBulkCreateSuccess,
  templateDownloadError,
  templetDowmloadLoading,
  setTemplateDownloadError
}: BulkUploadProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { caseId } = useParams<{ caseId: string }>();
  const { t } = useTranslation();
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [showProgress, setShowProgress] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [importJobId, setImportJobId] = useState<string | null>(null);
  const [getFileImportProgressStatus, { data: importStatusData }] =
    useLazyGetFileImportProgressStatusQuery();
  const [uploadDocument] = useBulkImportDocumentMutation();
  const [discardDetails, setDiscardDetails] = useState(false);

  function handleDiscardReject() {
    setDiscardDetails(false);
  }
  function handleDiscardApprove() {
    setDiscardDetails(false);
    handleClose();
  }
  const handleFileChange = (files: any) => {
    setFile(files[0]);
  };

  const handleClose = () => {
    if(importStatusData?.statusName === "Success" && onBulkCreateSuccess){
      onBulkCreateSuccess()
    }
    onClose();
  };

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (
      showProgress &&
      importJobId &&
      importStatusData?.statusName !== "Success"
    ) {
      intervalId = setInterval(() => {
        getFileImportProgressStatus({ importJobId });
      }, 10000);
    }
    if (importStatusData?.statusName === "Failed") {
      setUploadStatus("error");
      setErrorMessage(importStatusData?.errorMessage);
      setShowProgress(false);
    } else if (importStatusData?.statusName === "Success") {
      setUploadStatus("Success");
      setShowProgress(false);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [
    showProgress,
    importJobId,
    importStatusData,
    getFileImportProgressStatus,
  ]);

  const handleSubmit = async () => {
    setIsUploading(true);
    if (file) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("DisplayName", "test");
      formData.append("Entitytype", "2");
      if (caseId) {
        formData.append("CaseId", caseId);
      }
      try {
        const response = await uploadDocument(formData).unwrap();
        const importJobId = response;
        setImportJobId(importJobId);
        await getFileImportProgressStatus({ importJobId });
        setShowProgress(true);
        setUploadStatus("inProgress");
      } catch (error: any) {
        setUploadStatus("error");
        setErrorMessage(error?.data ?? undefined);
      } finally {
        setIsUploading(false);
      }
    }
  };

  return (
    <>
      <MotifModal
        onClose={() => {
          if (
            file &&
            (!uploadStatus || 
              (uploadStatus !== "Success" && uploadStatus !== "error" && uploadStatus!=="inProgress") )
          ) {
            setDiscardDetails(true);
          } else {
            handleClose();
          }
        }}
        show={showModal}
        size="lg"
        focusTrapOptions={{
          tabbableOptions: {
            displayCheck: "none",
          },
        }}
      >
        <MotifModalHeader
          closeButtonProps={{
            "aria-label": "Custom Close Button aria-label",
            title: "Custom Close Button title",
          }}
        >
          <span>{title}</span>
        </MotifModalHeader>
        {uploadStatus === "Success" ? (
          <SuccessMessage onClose={()=>{
            if(onBulkCreateSuccess){
            onBulkCreateSuccess()
            }
          
          onClose();
          }}  importJobId={importJobId} caseId={caseId}/>
        ) : uploadStatus === "error" ? (
          <ErrorMessage onClose={handleClose}  importJobId={importJobId} caseId={caseId} errorMessage={errorMessage} />
        ) : !showProgress ? (
          <>
            <MotifModalBody className="pad-t-10">
              {templateDownloadError && <NotificationGrid show={true} type="error" message={templateDownloadError} onClose={() =>{setTemplateDownloadError && setTemplateDownloadError("")}} />}
              <div className="motif-container case-summary-form pad-v-20 mar-t-20">
                <div className="motif-row justify-content-between align-items-center">
                  <div className="motif-col-4">
                    <div className="motif-body1-default-regular page-title">
                      {title}
                    </div>
                    <div className="motif-body2-default-regular page-description">
                      {description}
                    </div>
                  </div>
                  <div>
                  <CommonTooltipWrapper tooltipTitle={t("global.globalActions.viewOrDownload")}>
                    <MotifButton
                      onClick={()=>onDownload()}
                      size="small"
                      type="submit"
                      variant={DOWNLOAD_IMG_VARIANT}
                      loading={templetDowmloadLoading}
                    >
                    {getCommonDownloadImg()}
                    </MotifButton>
                  </CommonTooltipWrapper>
                  </div>
                </div>
              </div>
              <div className="pad-v-20 mar-t-20">
                <div className="motif-body1-default-regular pad-v-20">
                  {uploadLabel}
                </div>
                <MotifFileUploader
                  id="bulk-upload"
                  label={uploadLabel}
                  labelDescription={uploadDescription}
                  onDrop={handleFileChange}
                  accept={fileExtensions.import}
                  maxFiles={maxFiles}
                >
                  {file && (
                    <MotifFileUploaderItem
                      fileIcon={<MotifIcon src={actionIcDescription24px} />}
                      fileName={file.name}
                      uploaded={true}
                      uploadedText={
                        t("pages.claims.documentPopup.displayName") + file.name
                      }
                      error={
                        (file && file.size >= fileExtensions.maxFileSize) ||
                        false
                      }
                      errorText={t(
                        "pages.claims.stageStatusPopup.filesizeExceedsLimit",
                        {
                          filesize: "250",
                        }
                      )}
                    />
                  )}
                </MotifFileUploader>
              </div>
            </MotifModalBody>

            <MotifModalFooter>
              <div className="document-footer display-flex ">
                <MotifButton
                  type="button"
                  variant={CANCEL_BUTTON_VARIANT}
                  className="cancelBtn mar-r-10"
                  onClick={() => {
                    if (file) {
                      setDiscardDetails(true);
                    } else {
                      handleClose();
                    }
                  }}
                >
                  {t("global.globalActions.cancel")}
                </MotifButton>
                <MotifButton
                  onClick={handleSubmit}
                  variant={UPLOAD_BUTTON_VARIANT}
                  className="cancelBtn"
                  disabled={
                    isUploading || !file ||
                    (file && file.size >= fileExtensions.maxFileSize) ||
                    false
                  }
                >
                  {isUploading ? t("global.globalActions.uploading") : t("global.globalActions.upload")}
                </MotifButton>
              </div>
            </MotifModalFooter>
          </>
        ) : (
          <DataSourceUploadStatus importStatus={importStatusData} />
        )}
      </MotifModal>
      <ConfirmationModal
        isVisible={discardDetails}
        setIsVisible={handleDiscardReject}
        cancelBtnApproveHandler={handleDiscardApprove}
        cancelBtnRejectHandler={handleDiscardReject}
      />
    </>
  );
};

export default ClaimBulkImport;
